import * as A from '@core/arena'

import { DateFormats, Dates, I18n } from '@openforis/arena-core'

const {
  add,
  convertDate,
  diffInMinutes,
  diffInHours,
  diffInDays,
  diffInMonths,
  diffInWeeks,
  parse,
  parseISO,
  sub,
  subDays,
  subMonths,
  subYears,
} = Dates

export { add, convertDate, diffInHours, parse, parseISO, sub, subDays, subMonths, subYears }

import { isBlank } from './stringUtils'

export const formats = {
  dateDefault: DateFormats.dateDisplay,
  dateISO: DateFormats.dateStorage,
  datetimeDefault: DateFormats.datetimeDefault,
  datetimeExport: 'YYYY-MM-DD HH:mm:ss',
  datetimeDisplay: DateFormats.datetimeDisplay,
  datetimeISO: DateFormats.datetimeStorage,
  timeStorage: DateFormats.timeStorage,
  timeWithSeconds: DateFormats.timeWithSeconds,
} as const

const normalizeDateTimeValue =
  (length: number) =>
  (value: unknown): string =>
    A.pipe(A.ifElse(A.is(String), A.identity, A.toString), (val: string) => val.padStart(length, '0'))(value)

export const format = (date: unknown, format: string = formats.dateDefault): string =>
  Dates.format(date as Date, format)

/**
 * Get a relative date string (e.g., "2 weeks ago").
 */
export const getRelativeDate = (i18n: Pick<I18n, 't'>, date: unknown): string | null => {
  if (A.isNil(date)) {
    return null
  }

  const now = new Date()

  const formatDiff = (count: number, unit: string): string => i18n.t('common.date.timeDiff', { count, unit })

  const months = diffInMonths(now, date as Date)
  if (months > 0) {
    return format(date, 'DD MMM YYYY')
  }

  const weeks = diffInWeeks(now, date as Date)
  if (weeks > 0) {
    return formatDiff(weeks, 'week')
  }

  const days = diffInDays(now, date as Date)
  if (days > 0) {
    return formatDiff(days, 'day')
  }

  const hours = diffInHours(now, date as Date)
  if (hours > 0) {
    return formatDiff(hours, 'hour')
  }

  const minutes = diffInMinutes(now, date as Date)
  if (minutes > 10) {
    return formatDiff(minutes, 'minute')
  }

  return i18n.t('common.date.aMomentAgo')
}

export const isValidDateObject = Dates.isValidDateObject

/**
 * Checks if the date is valid. Takes into account leap years
 * (i.e. 2015/2/29 is not valid).
 * Used for PostgreSQL date inserts.
 */
export const isValidDate = (year: unknown, month: unknown, day: unknown): boolean => {
  if (isBlank(year) || isBlank(month) || isBlank(day)) {
    return false
  }

  const date = new Date(Number(year), Number(month) - 1, Number(day))

  return (
    Boolean(isValidDateObject(date)) &&
    date.getFullYear() === Number(year) &&
    date.getMonth() + 1 === Number(month) &&
    date.getDate() === Number(day)
  )
}

/**
 * Check if time (hour:minute:seconds) is valid. Seconds default to a valid 0 when omitted.
 */
export const isValidTime = (hour: unknown = '', minutes: unknown = '', seconds: unknown = 0): boolean => {
  if (isBlank(hour) || isBlank(minutes)) return false
  if (!(Number(hour) >= 0 && Number(hour) < 24 && Number(minutes) >= 0 && Number(minutes) < 60)) return false
  return Number(seconds) >= 0 && Number(seconds) < 60
}

/**
 * Check if a date string is valid for the given format.
 */
export const isValidDateInFormat = (dateStr: string, format: string): boolean => {
  const parsed = parse(dateStr, format as any)
  return isValidDateObject(parsed)
}

export const formatDateISO = (date: unknown): string => format(date, formats.dateISO)

export const formatDateTimeDefault = (date: unknown): string => format(date, formats.datetimeDefault)

export const formatDateTimeDisplay = (date: unknown): string => format(date, formats.datetimeDisplay)

export const formatDateTimeExport = (date: unknown): string => format(date, formats.datetimeExport)

export const formatTime = (hour: unknown, minute: unknown, seconds?: unknown): string => {
  const base = `${normalizeDateTimeValue(2)(hour)}:${normalizeDateTimeValue(2)(minute)}`
  return seconds === undefined ? base : `${base}:${normalizeDateTimeValue(2)(seconds)}`
}

export const getTimeFormat = (includeSeconds: boolean): string =>
  includeSeconds ? formats.timeWithSeconds : formats.timeStorage

export const parseDateISO = (dateStr: string): unknown => parse(dateStr, formats.dateISO)

export const convertDateFromISOToDisplay = (dateStr: string): unknown =>
  convertDate({
    dateStr,
    formatFrom: formats.dateISO,
    formatTo: formats.dateDefault,
  })

/**
 * Formats an ISO (UTC) timestamp for display in the local time zone of the browser.
 */
export const convertDateTimeFromISOToDisplay = (dateStr: string): unknown =>
  convertDate({
    dateStr,
    formatFrom: formats.datetimeISO,
    formatTo: formats.datetimeDisplay,
    keepTimeZone: false,
  })

export const nowFormatDefault = (): string => format(Date.now(), formats.datetimeDefault)
